import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

export default function SMSProgramPage() {
  usePageTitle("SMS Compliance | CleanBid.ai");
  return (
    <div className="bg-[#0A0A0F] pt-20" data-testid="sms-program-page">
      <div className="border-b border-[#1E1E2E] py-14">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <p className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold mb-3">Compliance</p>
          <h1 className="text-4xl font-bold tracking-tight text-[#F4F4F8] mb-2">
            CleanBid SMS Messaging Program
          </h1>
          <p className="text-[#8B8BA7] text-sm">Last updated: January 1, 2026</p>
        </div>
      </div>

      <article className="py-12">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl space-y-10 text-[15px] leading-relaxed">

          <section>
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Program Name</h2>
            <p className="text-[#8B8BA7]">CleanBid SMS Messaging Program</p>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Program Description</h2>
            <p className="text-[#8B8BA7]">
              CleanBid is a software platform that enables home service businesses (including residential cleaning,
              commercial cleaning, lawn care, pest control, and exterior services) to communicate with their customers
              via SMS. Businesses using CleanBid send messages through a dedicated local business number provisioned
              through the CleanBid platform.
            </p>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Message Types</h2>
            <p className="text-[#8B8BA7] mb-3">
              Messages sent through the CleanBid platform may include the following types:
            </p>
            <ul className="space-y-2 text-[#8B8BA7]">
              {[
                "Quote delivery — sending a service quote to a prospective customer in response to an inquiry",
                "Follow-up reminders — scheduled follow-up messages on open or unanswered quotes",
                "Job status updates — notifications to customers regarding job creation, scheduling, or completion",
                "Review requests — automated requests for customer reviews sent upon job completion",
                "Support communications — responses to customer inquiries submitted via SMS",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1E1E2E] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Who Receives Messages</h2>
            <p className="text-[#8B8BA7]">
              SMS messages are sent to customers of businesses that use the CleanBid platform. Recipients are
              individuals who have provided express written consent to receive SMS communications from the
              specific business contacting them. Consent is collected by the business operator prior to sending
              any messages through CleanBid.
            </p>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Opt-In Method</h2>
            <p className="text-[#8B8BA7]">
              Customers opt in to receive SMS messages from a business using CleanBid through one or more of
              the following methods:
            </p>
            <ul className="space-y-2 text-[#8B8BA7] mt-3">
              {[
                "Completion of a web-based inquiry or quote request form that includes SMS consent language",
                "In-person agreement at the time of service, documented by the business operator in CleanBid",
                "Verbal consent provided to the business operator and recorded in the customer's profile",
                "Inbound SMS initiated by the customer to the business's CleanBid number",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1E1E2E] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-[#8B8BA7] mt-3">
              CleanBid requires all platform users to confirm that recipients have provided appropriate consent
              before using the platform to send messages. Business operators are solely responsible for obtaining
              and documenting consent in compliance with applicable law.
            </p>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <div className="bg-[#111118] border border-[#71DED6]/30 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-[#F4F4F8]">Opt-Out Instructions</h2>
              <p className="text-[#8B8BA7]">
                Recipients may opt out of SMS messages at any time by replying{" "}
                <span className="text-[#F4F4F8] font-mono font-semibold">STOP</span> to any message. Upon receipt
                of a STOP message, no further messages will be sent to that number. To re-subscribe, recipients
                may reply <span className="text-[#F4F4F8] font-mono font-semibold">START</span>.
              </p>

              <h2 className="text-lg font-semibold text-[#F4F4F8] pt-2">Help Instructions</h2>
              <p className="text-[#8B8BA7]">
                Recipients may reply <span className="text-[#F4F4F8] font-mono font-semibold">HELP</span> to
                any message to receive assistance. For additional support, contact CleanBid at{" "}
                <a href="mailto:info@cleanbid.ai" className="text-[#71DED6] hover:underline">
                  info@cleanbid.ai
                </a>.
              </p>
            </div>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Message Frequency</h2>
            <p className="text-[#8B8BA7]">
              Message frequency varies based on the level of job activity between the customer and the business.
              Customers may receive messages related to quote requests, follow-ups on open quotes, job status
              updates, and review requests. Messaging is tied to actual business activity and is not sent on a
              fixed recurring schedule.
            </p>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Message and Data Rates</h2>
            <p className="text-[#8B8BA7]">
              Message and data rates may apply. Standard carrier messaging rates apply based on the recipient's
              mobile plan. CleanBid is not responsible for any carrier charges incurred by message recipients.
            </p>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Privacy and Data Use</h2>
            <p className="text-[#8B8BA7]">
              Phone numbers and SMS opt-in data collected through this program are not sold, shared, or
              transferred to third parties for marketing purposes. Carrier information and messaging data
              are used solely for the purpose of facilitating the described messaging program.
            </p>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Carrier Disclaimer</h2>
            <p className="text-[#8B8BA7]">
              Carriers are not liable for delayed or undelivered messages. SMS delivery is subject to carrier
              network availability, routing, and policies. CleanBid does not guarantee delivery of any message.
            </p>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Related Policies</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/privacy"
                className="text-[#71DED6] hover:underline text-sm"
                data-testid="sms-privacy-link"
              >
                Privacy Policy →
              </Link>
              <Link
                to="/terms"
                className="text-[#71DED6] hover:underline text-sm"
                data-testid="sms-tos-link"
              >
                Terms of Service →
              </Link>
            </div>
          </section>

          <section className="border-t border-[#1E1E2E] pt-8">
            <h2 className="text-lg font-semibold text-[#F4F4F8] mb-3">Contact</h2>
            <div className="bg-[#111118] border border-[#1E1E2E] rounded-xl p-5 space-y-1.5 text-[#8B8BA7]">
              <p><span className="text-[#F4F4F8] font-medium">CleanBid.ai</span></p>
              <p>SMS Support: <a href="mailto:info@cleanbid.ai" className="text-[#71DED6] hover:underline">info@cleanbid.ai</a></p>
              <p>Website: <a href="https://mycleanbid.ai" className="text-[#71DED6] hover:underline">mycleanbid.ai</a></p>
            </div>
          </section>

        </div>
      </article>
    </div>
  );
}
